
import { useState, useCallback, useEffect } from 'react';
import { Article, Journalist } from '@/components/reader-home/types';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/components/ui/use-toast';
import { useJournalistFollowService } from './useJournalistFollowService';
import { useArticleService } from './useArticleService';
import { useTopicService } from './useTopicService';
import { useArticleProgressService } from './useArticleProgressService';

export const useReaderHomeService = () => {
  const { user } = useAuth();
  const [articles, setArticles] = useState<Article[]>([]);
  const [followedJournalists, setFollowedJournalists] = useState<Journalist[]>([]);
  const [topics, setTopics] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Import services
  const { loadFollowedJournalists, toggleFollowJournalist } = useJournalistFollowService(setFollowedJournalists);
  const { loadArticles } = useArticleService(setArticles);
  const { loadTopics } = useTopicService(setTopics);
  const { updateArticleReadProgress } = useArticleProgressService();

  // Load all data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        loadArticles(user),
        loadFollowedJournalists(user),
        loadTopics()
      ]);
    } catch (err) {
      console.error('Error loading home data:', err);
      setError(err instanceof Error ? err : new Error('Failed to load data'));
      toast({
        title: 'Error loading content',
        description: 'There was a problem loading the latest content. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [loadArticles, loadFollowedJournalists, loadTopics, user]);

  // Load data on initial mount and when user auth changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    articles,
    followedJournalists,
    topics,
    loading,
    error,
    toggleFollowJournalist,
    updateArticleReadProgress,
    refreshData: loadData
  };
};
